import { z } from 'zod';
import { getDefaultStore } from 'jotai';
import { AddressSchema } from '@/modules/address-schema';
import { accessTokenAtom } from '@/modules/sanr-api';
import { fetchJson } from '@/modules/fetch-core';
import {
  parseRowsWithDegrade,
  type MalformedRow,
} from '@/modules/malformed-row';
import { safeFloat } from '@/modules/safe-float';
import { pct, ratio01, type Pct } from '@/modules/brands';

// The v2 predictions REST client — /v2/predictions*. Spec:
// scripts/swagger-snapshot/sanr.json (see the swagger-snapshot gate). The
// list endpoints' `filter` param is the same brace-less Mongo-style
// passthrough as the other queryParser APIs: no $in/$eq — multiple statuses
// go as an $or of equalities (verified live 2026-08-12).

const predictionBase = {
  id: z.coerce.string(), // numeric on the wire
  marketId: z.string(),
  issuerId: z.string(),
  ipnsKeyId: z.string().nullable().optional(),
  symbol: z.string(),
  slug: z.string(),
  direction: z.enum(['up', 'down']),
  leverage: z.number(),
  // On an open row this is NOT a close: the running current price ships in
  // currentPrice and closePrice stays null until the position closes —
  // consumers derive the displayed "now" price as closePrice ?? currentPrice
  // on that basis, and must gate real-close displays on status/closedAt,
  // never on closePrice != null.
  closePrice: safeFloat.nullable(),
  performance: ratio01,
  // No per-status guarantee: live expired rows shipped it NON-null while
  // cancelled rows shipped null, and ~6% of sampled closed rows shipped
  // null — never treat presence as a lifecycle signal. The looseness is
  // deliberate; don't tighten.
  currentPrice: safeFloat.nullable(),
  stopLossPrice: safeFloat.nullable(),
  takeProfitPrice: safeFloat.nullable(),
  openedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  // Backend ships a non-null default ("user_closed_signal") on EVERY status
  // — only meaningful alongside status 'closed'; never read it as a
  // closed-ness signal. Deliberately un-tightened.
  closeType: z.string().nullable(),
  createdAt: z.string(),
  // The embedded issuer follows the spec's IssuerSummary: address is
  // OPTIONAL there (and null-prone for walletless SSO accounts), so both
  // absent and null parse — a walletless user's prediction must stay a real
  // row, not a malformed-row degrade. A present value is checksummed at
  // this boundary, and a non-address string degrades its ONE row — consumers
  // never re-validate it, so a throw downstream would reject a whole list.
  issuer: z.looseObject({
    address: AddressSchema.nullable().optional(),
    username: z.string(),
    avatar: z.string().nullable(),
  }),
  // The embedded market is the row's source of truth for KOL identity,
  // lifecycle dates, and on-chain prices — no market-feed roundtrip needed.
  market: z.looseObject({
    id: z.coerce.string(), // numeric on the wire
    asset: z.string(),
    quoteAsset: z.string(),
    direction: z.enum(['up', 'down']),
    entryPrice: safeFloat,
    resolvePrice: safeFloat.nullable(),
    startedAt: z.string(),
    // unread-field-allow: consumed via @/modules/market-status — the embedded market passes whole into its structurally-typed param
    votingEndsAt: z.string(),
    resolvesAt: z.string(),
    tweetText: z.string(),
    sourceUrl: z.string(),
    tweetImages: z.array(z.string()),
    imageUrl: z.string().nullable(),
    logoUrl: z.string().nullable(),
    kol: z.looseObject({
      name: z.string(),
      handle: z.string(),
      avatarUrl: z.string().nullable(),
    }),
  }),
};

// Union-claimed NON-NULL numerics must reject null/absent instead of
// inheriting safeFloat's Number(null) === 0 coercion — a null openPrice on
// an "open" row is exactly the drift the per-status strictness exists to
// surface. Non-numeric junk still fails inside safeFloat itself.
const strictFloat = z
  .unknown()
  .refine((v): boolean => v != null)
  .pipe(safeFloat);

// One prediction row as every endpoint returns it, split by the backend
// lifecycle status. All FIVE wire states are declared (the swagger-snapshot
// gate pins the set; a `cancelled` row genuinely comes back from GET reads
// — see deletePrediction). Per-variant strictness encodes exactly the
// correlations the spec documents AND live probing confirmed
// (verified live 2026-08-11). Beyond what the variants show: on open/closed
// rows a non-null triggerPrice means limit-originated and the value SURVIVES
// entry — consumers render the limit-order marker from it; a closed row's
// beatKOL stays null until the market also resolves — consumers fall back to
// a live computation while null. Nothing else varies by status — see the
// predictionBase field comments for the correlations the spec does NOT
// document.
export const UserPredictionSchema = z.discriminatedUnion('status', [
  z.looseObject({
    ...predictionBase,
    status: z.literal('open'),
    openPrice: strictFloat,
    triggerPrice: safeFloat.nullable(),
    beatKOL: z.null(),
  }),
  // beatKOL = performance × leverage − market.kolPerformance (the swagger's
  // field text states the opposite difference; this formula matched 60 of 60
  // sampled closed rows, verified live 2026-09-24) — still null until the
  // market also resolves.
  z.looseObject({
    ...predictionBase,
    status: z.literal('closed'),
    openPrice: strictFloat,
    triggerPrice: safeFloat.nullable(),
    beatKOL: ratio01.nullable(),
  }),
  // Pending trigger (limit) order awaiting its triggerPrice.
  z.looseObject({
    ...predictionBase,
    status: z.literal('created'),
    openPrice: z.null(),
    triggerPrice: strictFloat,
    beatKOL: z.null(),
  }),
  // Trigger order whose market voting closed before it fired.
  z.looseObject({
    ...predictionBase,
    status: z.literal('expired'),
    openPrice: z.null(),
    triggerPrice: strictFloat,
    beatKOL: z.null(),
  }),
  // Trigger order the user cancelled (DELETE) before it opened.
  z.looseObject({
    ...predictionBase,
    status: z.literal('cancelled'),
    openPrice: z.null(),
    triggerPrice: strictFloat,
    beatKOL: z.null(),
  }),
]);

export type UserPrediction = z.output<typeof UserPredictionSchema>;

// The list envelope parses fail-open as a whole; rows stay unknown here and
// parse ROW-LEVEL through @/modules/malformed-row (source 'v2') so one bad
// row degrades alone.
const ListEnvelopeSchema = z.looseObject({
  data: z.array(z.unknown()),
  total: z.number(),
});

function parsePredictionRows(
  endpointId: string,
  rows: unknown[],
): (UserPrediction | MalformedRow)[] {
  return parseRowsWithDegrade('v2', endpointId, UserPredictionSchema, rows);
}

const store = getDefaultStore();

function baseUrl(): string {
  // v1 BACKEND_URL_API ends with `/v1`; strip it to get the origin
  // (environment precondition; prod vs stage is baked in at build time).
  // Stryker disable next-line Regex: equivalent — every real base value (prod/stage/test) contains '/v1' only as the trailing segment, so anchored and first-occurrence replace coincide; the anchor documents intent
  return import.meta.env.VITE_BACKEND_URL_API.replace(/\/v1$/, '');
}

type FetchOpts = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal | undefined;
  authed?: boolean;
};

// Auth is a bearer token read from @/modules/sanr-api's accessTokenAtom AT
// CALL TIME, with NO auth-ready gating — unlike the v1 client's latched
// entry, calls fire immediately with whatever token the atom currently
// holds (absent token = no Authorization header). `authed: false` reads
// deliberately OMIT the header even when a token exists: the endpoint
// scopes authed reads to the caller, and the public listings need the
// unscoped view.
function v2Fetch<T>(
  endpointId: string,
  path: string,
  opts: FetchOpts,
  schema: z.ZodType<T>,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (opts.authed !== false) {
    const token = store.get(accessTokenAtom);
    if (token != null && token !== '') {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  return fetchJson({
    backend: 'sanr',
    source: 'v2',
    key: endpointId,
    url: `${baseUrl()}${path}`,
    init: {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body != null ? JSON.stringify(opts.body) : null,
      signal: opts.signal ?? null,
    },
    schema,
    requestBody: opts.body,
  });
}

// POST /v2/predictions (authed) — create. The market-order body variant is
// born `open`; the trigger-order variant is born `created`, with
// openWhenPriceMoreThen picking the fire side (true → fires when price ≥
// trigger, false → ≤) — consumers compute the flag from the live price vs
// the trigger, so a semantics flip inverts every limit order's fire side;
// the entered-vs-pending UI split builds on the born status.
export function createPrediction(
  body: {
    marketId: string;
    direction: 'up' | 'down';
    stopLossPrice?: number | null;
    takeProfitPrice?: number | null;
    leverage?: number;
  } & (
    | { triggerPrice?: undefined; openWhenPriceMoreThen?: undefined }
    | { triggerPrice: number; openWhenPriceMoreThen: boolean }
  ),
): Promise<UserPrediction> {
  return v2Fetch(
    'POST_PREDICTION',
    '/v2/predictions',
    { method: 'POST', body },
    UserPredictionSchema,
  );
}

// PUT /v2/predictions/{id} (authed) — status-scoped: the
// SL/TP-only body variant is what an `open` position accepts; the full
// variant is what a `created` order accepts; any other status → 400. Which
// variant is legal depends on the EDITED ROW's status, which types can't
// carry — consumers must match the variant to the row (and disable the
// rejected fields when editing an open position).
export function updatePrediction(
  id: string,
  body:
    | {
        stopLossPrice: number | null;
        takeProfitPrice: number | null;
        triggerPrice?: undefined;
        leverage?: undefined;
        openWhenPriceMoreThen?: undefined;
      }
    | {
        stopLossPrice: number | null;
        takeProfitPrice: number | null;
        triggerPrice: number;
        leverage: number;
        openWhenPriceMoreThen: boolean;
      },
): Promise<UserPrediction> {
  return v2Fetch(
    'PUT_PREDICTION',
    `/v2/predictions/${id}`,
    { method: 'PUT', body },
    UserPredictionSchema,
  );
}

type PredictionStatus = 'open' | 'closed' | 'created' | 'expired' | 'cancelled';

// Public read of EVERY prediction by one issuer (any user, not just the
// caller) — auto-paginated in take=500 pages until the reported total is
// reached; an empty page also terminates, so an overstated server total
// can never loop requests forever. Multiple statuses become an $or of
// equalities; a single status a plain equality; no status filter when
// omitted — an UNFILTERED read returns `cancelled` rows too (verified live 2026-08-11),
// so callers listing "everything" must expect that variant.
export async function listAllIssuerPredictions(params: {
  issuerId: string;
  status?: PredictionStatus | readonly PredictionStatus[];
  sort?: string;
  signal?: AbortSignal;
}): Promise<(UserPrediction | MalformedRow)[]> {
  const pageSize = 500;
  const filterParts = [`"issuerId":"${params.issuerId}"`];
  const { status } = params;
  if (typeof status === 'string') {
    filterParts.push(`"status":"${status}"`);
  } else if (status !== undefined) {
    filterParts.push(
      `"$or":[${status.map((s) => `{"status":"${s}"}`).join(',')}]`,
    );
  }
  const filter = filterParts.join(',');

  const all: (UserPrediction | MalformedRow)[] = [];
  let skip = 0;
  while (true) {
    const qs = new URLSearchParams();
    qs.set('take', String(pageSize));
    qs.set('skip', String(skip));
    qs.set('filter', filter);
    if (params.sort != null && params.sort !== '') qs.set('sort', params.sort);
    const { data, total } = await v2Fetch(
      'GET_ISSUER_PREDICTIONS',
      `/v2/predictions?${qs}`,
      { signal: params.signal, authed: false },
      ListEnvelopeSchema,
    );
    all.push(...parsePredictionRows('GET_ISSUER_PREDICTIONS', data));
    skip += pageSize;
    if (all.length >= total || data.length === 0) return all;
  }
}

const VISIBLE_STATUS_FILTER =
  '"$or":[{"status":"open"},{"status":"closed"},{"status":"created"}]';

// Public single-shot (take=500) read of one issuer's predictions on one
// market, newest first. The request filters to the visible-status set —
// open/closed/created — so consumers may narrow returned rows to exactly
// those three (status-coercion and counting logic builds on it; an
// expired/cancelled leak is the phantom-open-call failure mode).
export async function listIssuerMarketPredictions(params: {
  marketId: string;
  issuerId: string;
  signal?: AbortSignal;
}): Promise<{
  data: (UserPrediction | MalformedRow)[];
}> {
  const qs = new URLSearchParams();
  qs.set('take', '500');
  qs.set(
    'filter',
    `"marketId":"${params.marketId}","issuerId":"${params.issuerId}",${VISIBLE_STATUS_FILTER}`,
  );
  qs.set('sort', 'createdAt:desc');
  const { data } = await v2Fetch(
    'GET_ISSUER_MARKET_PREDICTIONS',
    `/v2/predictions?${qs}`,
    { signal: params.signal, authed: false },
    ListEnvelopeSchema,
  );
  return {
    data: parsePredictionRows('GET_ISSUER_MARKET_PREDICTIONS', data),
  };
}

// Public paginated listing of all predictions on a market (take defaults
// 50, skip 0), sorted predictionChangedAt:desc. Same visible-status set as
// above — pending `created` trigger orders deliberately count toward the
// listing and its total; `expired` and `cancelled` are
// excluded, which also keeps the paginated total honest.
export async function listMarketPredictions(params: {
  marketId: string;
  take?: number;
  skip?: number;
  signal?: AbortSignal;
}): Promise<{
  data: (UserPrediction | MalformedRow)[];
  total: number;
}> {
  const qs = new URLSearchParams();
  qs.set('take', String(params.take ?? 50));
  qs.set('skip', String(params.skip ?? 0));
  qs.set('filter', `"marketId":"${params.marketId}",${VISIBLE_STATUS_FILTER}`);
  qs.set('sort', 'predictionChangedAt:desc');
  const { data, total } = await v2Fetch(
    'GET_MARKET_PREDICTIONS',
    `/v2/predictions?${qs}`,
    { signal: params.signal, authed: false },
    ListEnvelopeSchema,
  );
  return {
    data: parsePredictionRows('GET_MARKET_PREDICTIONS', data),
    total,
  };
}

const PerformanceChartSchema = z.array(
  z.looseObject({
    v: pct, // cumulative PnL = performance × leverage × 100
    date: z.string().nullable(),
  }),
);

// GET /v2/predictions/performance-chart?issuerId= (public) — the equity
// curve, keyed by the SanR issuer UUID (works for walletless SSO users; the
// endpoint alternatively takes address or username — exactly one identifier
// is required). An unknown issuerId is a 404 ("Issuer not found"), which
// throws — an empty array means a known issuer with no closed predictions,
// never a missing one (verified live 2026-08-20). Point order and placement
// are backend-owned and rendered as-is. Rare null-date points are dropped at
// the boundary so consumers can type-assume `date: string`.
export async function getPerformanceChart(
  issuerId: string,
  signal?: AbortSignal,
): Promise<
  {
    v: Pct;
    date: string;
  }[]
> {
  const qs = new URLSearchParams({ issuerId });
  const points = await v2Fetch(
    'GET_PERFORMANCE_CHART',
    `/v2/predictions/performance-chart?${qs}`,
    { signal, authed: false },
    PerformanceChartSchema,
  );
  return points.filter((p): p is { v: Pct; date: string } => p.date !== null);
}

const GroupedBySymbolSchema = z.array(
  z.looseObject({
    symbol: z.string(),
    amount: safeFloat, // numeric string on the wire, coerced
  }),
);

// GET /v2/predictions/grouped-by-symbol?issuerId= (public) — per-symbol
// prediction counts (pair format, e.g. "BTC/USD"), keyed by the SanR issuer
// UUID (same identifier contract as getPerformanceChart: unknown issuerId is
// a thrown 404, empty array = known issuer with nothing to count). WHICH
// statuses count is backend-owned — counts are rendered as-is; don't build
// logic on the counting basis. The observed rule flipped once (verified live
// 2026-08-06: every status counted; verified live 2026-08-11: exactly the
// open+closed subset) — the argument for claiming no rule.
export function getPredictionsGroupedBySymbol(
  issuerId: string,
  signal?: AbortSignal,
): Promise<
  {
    symbol: string;
    amount: number;
  }[]
> {
  const qs = new URLSearchParams({ issuerId });
  return v2Fetch(
    'GET_PREDICTIONS_GROUPED_BY_SYMBOL',
    `/v2/predictions/grouped-by-symbol?${qs}`,
    { signal, authed: false },
    GroupedBySymbolSchema,
  );
}

const DeletePredictionSchema = z.unknown();

// DELETE /v2/predictions/{id} (authed) — cancel a pending `created` trigger
// order before it fires (400 on any other status). Sets the row's status to
// `cancelled`; the row is NOT deleted — it keeps coming
// back from GET reads, status-filtered and unfiltered alike (verified live 2026-08-11 —
// don't reintroduce the "soft-deleted out of every query" assumption),
// and only the visible-status filters keep it out of the market
// listings above. Frees the market for a new order — consumers re-offer
// prediction creation on the market after a cancel. Deliberately no field
// contract — don't read the response body.
export function deletePrediction(id: string): Promise<unknown> {
  return v2Fetch(
    'DELETE_PREDICTION',
    `/v2/predictions/${id}`,
    { method: 'DELETE' },
    DeletePredictionSchema,
  );
}

// POST /v2/predictions/{id}/close (authed) — close an OPEN position at the
// current market price; returns the updated row. Cancelling a pending
// order is deletePrediction above.
export function closePrediction(id: string): Promise<UserPrediction> {
  return v2Fetch(
    'POST_PREDICTION_CLOSE',
    `/v2/predictions/${id}/close`,
    { method: 'POST' },
    UserPredictionSchema,
  );
}
